import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Play, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useListeningRulesQuery,
  useCreateListeningRuleMutation,
  useUpdateListeningRuleMutation,
  useSourceAccountsQuery,
  useTrackedKeywordsQuery,
} from '@/client/api/marketing/sources';
import { useDiscoverCoverageMutation } from '@/client/api/marketing/radar';
import {
  compileWatchers,
  ruleNameForWatcher,
  unsyncedWatchers,
  type WatchRelevance,
  type WatcherKind,
} from './compile-watchers';
import { ListeningHealthStrip } from './ListeningHealthStrip';

const KIND_LABEL: Record<WatcherKind, string> = {
  author_feed: 'Their posts',
  mention: 'Mentions of us',
  keyword: 'Keyword / hashtag',
  rule: 'Custom rule',
};

const KIND_TONE: Record<WatcherKind, string> = {
  author_feed: 'bg-violet-50 text-violet-700',
  mention: 'bg-sky-50 text-sky-700',
  keyword: 'bg-amber-50 text-amber-800',
  rule: 'bg-gray-100 text-gray-600',
};

function normQuery(q: string): string {
  return q.replace(/\s+/g, ' ').trim().toLowerCase();
}

export function AutoWatchPanel() {
  const accounts = useSourceAccountsQuery({ limit: 200 });
  const keywords = useTrackedKeywordsQuery({ limit: 100 });
  const rules = useListeningRulesQuery({ limit: 200 });
  const createRule = useCreateListeningRuleMutation();
  const updateRule = useUpdateListeningRuleMutation();
  const discover = useDiscoverCoverageMutation();

  const [relevance, setRelevance] = useState<WatchRelevance>('relevant');
  const [syncing, setSyncing] = useState(false);

  const watchers = useMemo(
    () =>
      compileWatchers({
        accounts: accounts.data?.items ?? [],
        keywords: keywords.data?.items ?? [],
        rules: rules.data?.items ?? [],
        relevance,
      }),
    [accounts.data, keywords.data, rules.data, relevance],
  );

  const missing = unsyncedWatchers(watchers);
  const ruleItems = rules.data?.items ?? [];
  const staleQuery = watchers.filter((w) => {
    if (w.matchingRuleId == null || w.kind === 'rule' || w.kind === 'keyword') return false;
    const rule = ruleItems.find((r) => r.lrid === w.matchingRuleId);
    return rule != null && normQuery(rule.query_include) !== normQuery(w.queryInclude);
  });
  const syncWork = missing.length + staleQuery.length;

  const listenCount = (accounts.data?.items ?? []).filter(
    (a) => a.active && a.use_as_listen && !a.owned,
  ).length;

  const syncMissing = async () => {
    if (syncWork === 0) {
      toast.message('Every auto-watcher already has a listening rule.');
      return;
    }
    setSyncing(true);
    let ok = 0;
    let fail = 0;

    for (const w of missing) {
      try {
        await createRule.mutateAsync({
          name: ruleNameForWatcher(w.name, w.key),
          query_include: w.queryInclude,
          query_exclude: w.queryExclude,
          channels: w.channels,
          alert_on_match: 1,
          alert_volume_spike: 0,
          alert_sentiment_drop: 0,
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }

    for (const w of staleQuery) {
      if (w.matchingRuleId == null) continue;
      try {
        await updateRule.mutateAsync({
          lrid: w.matchingRuleId,
          name: ruleNameForWatcher(w.name, w.key),
          query_include: w.queryInclude,
          query_exclude: w.queryExclude,
          channels: w.channels,
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }

    setSyncing(false);
    if (ok) toast.success(`Synced ${ok} listening rule${ok === 1 ? '' : 's'} from watchers`);
    if (fail) toast.error(`${fail} rule${fail === 1 ? '' : 's'} failed to sync`);
  };

  const runNow = () => {
    toast.message('Sweeping…', {
      description: 'Discovery is queued — this can take a minute.',
      id: 'radar-discover-sweep',
    });
    discover.mutate(
      { relevance, limit_per_query: 50 },
      {
        onSuccess: (res) => {
          toast.dismiss('radar-discover-sweep');
          const d = res?.data;
          if (!d) {
            toast.success('Discovery finished');
            return;
          }
          const errCount = d.errors?.length ?? 0;
          const base = `Discovery: ${d.ingested} new, ${d.duplicates} already in Radar, ${d.queried} queries`;
          if (errCount > 0) {
            const sample = d.errors![0]?.message ?? 'query failed';
            toast.message(base, {
              description: `${errCount} query error${errCount === 1 ? '' : 's'} — ${sample}`,
            });
          } else {
            toast.success(base);
          }
        },
        onError: (err) => {
          toast.dismiss('radar-discover-sweep');
          const status =
            (err as { status?: number; response?: { status?: number } })?.status ??
            (err as { response?: { status?: number } })?.response?.status;
          const msg = getApiErrorMessage(
            err,
            status === 429
              ? 'Discovery is rate-limited — wait before running again.'
              : 'Could not run discovery. Sync watchers first if rules are missing.',
          );
          toast.error(msg);
        },
      },
    );
  };

  return (
    <div className="mb-4 space-y-3">
      <ListeningHealthStrip />

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">Auto-pull into Radar</h2>
            <p className="mt-0.5 max-w-2xl text-xs text-gray-500">
              Compile listen-target feeds and @mentions of owned handles into queries. Topics saved
              in Trends already have a listening rule — Sync here is for listen-target handles. Run
              discovery asks the ingest beat to fetch matching public posts now. Instagram has no
              caption keyword search (hashtag + tracked accounts only); a term/phrase skip is a
              capability fact, not a quiet week.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={syncing || createRule.isPending || updateRule.isPending}
              onClick={() => void syncMissing()}
            >
              {syncing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Sync {syncWork > 0 ? `${syncWork} pending` : 'watchers'}
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              disabled={discover.isPending || watchers.length === 0}
              onClick={runNow}
            >
              {discover.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              Run discovery
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 bg-gray-50 px-4 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            From listen targets ({listenCount})
          </p>
          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
            {(
              [
                ['relevant', 'Only keyword / mention matches'],
                ['all_from_targets', 'Every public post they publish'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setRelevance(id)}
                className={cn(
                  'rounded px-2.5 py-1 text-xs font-medium',
                  relevance === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <Link
            to="/admin/marketing/sources/accounts"
            className="ml-auto text-[11px] font-medium text-blue-700 hover:underline"
          >
            Manage listen handles
          </Link>
        </div>

        {watchers.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">
            Flag competitor or partner accounts as listen targets, add keywords on Coverage, or create
            a custom rule below. Those become the auto-pull set.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-gray-400">
                <tr>
                  <th className="px-4 py-2 font-medium">Watcher</th>
                  <th className="px-3 py-2 font-medium">Kind</th>
                  <th className="px-3 py-2 font-medium">Query the beat will run</th>
                  <th className="px-3 py-2 font-medium">Rule</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {watchers.map((w) => {
                  const needsQuery =
                    w.matchingRuleId != null &&
                    staleQuery.some((s) => s.key === w.key);
                  return (
                    <tr key={w.key}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-900">{w.name}</p>
                        <p className="text-[11px] text-gray-400">{w.origin}</p>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={cn(
                            'inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium',
                            KIND_TONE[w.kind],
                          )}
                        >
                          {KIND_LABEL[w.kind]}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <code className="break-all text-xs text-gray-700">{w.queryInclude}</code>
                      </td>
                      <td className="px-3 py-2.5 text-xs">
                        {w.matchingRuleId != null ? (
                          needsQuery ? (
                            <span className="text-amber-700">Query out of date</span>
                          ) : (
                            <span className="text-emerald-700">Synced</span>
                          )
                        ) : (
                          <span className="text-amber-700">Not a rule yet</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
